import { useState } from 'react'
import { APIProvider, Map, AdvancedMarker, InfoWindow } from '@vis.gl/react-google-maps'
import { compDisplayPrice } from './compPrice'

const MAPS_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY
const MAP_ID   = 'c82c994f2480651a'
const DEFAULT_CENTER = { lat: 39.7392, lng: -104.9903 }

const fmtPrice = (n) =>
  n == null ? '—'
  : new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(n)

const fmtGlyph = (n) => {
  if (n == null) return '—'
  const rounded = n >= 1_000_000
    ? Math.round(n / 100_000) * 100_000
    : Math.round(n / 1_000)   * 1_000
  if (rounded >= 1_000_000) return '$' + (rounded / 1_000_000).toFixed(2).replace(/\.0$/, '') + 'M'
  return (rounded / 1_000) + 'K'
}

// ── Price pill marker (comp) ───────────────────────────────────────────────────
function CompMarker({ comp, active }) {
  const s = comp.status?.toLowerCase() ?? ''
  const statusColor = s === 'sold' ? '#4CBC18' : s === 'active' ? '#C13A4A' : '#F5C312'
  const bg = active ? '#17270D' : statusColor
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', cursor: 'pointer', userSelect: 'none' }}>
      <div style={{
        background: bg,
        padding: '5px 10px',
        borderRadius: 999,
        boxShadow: '0 2px 6px rgba(0,0,0,0.35)',
        whiteSpace: 'nowrap',
      }}>
        <span style={{ color: '#fff', fontSize: 12, fontWeight: 600, fontFamily: 'Figtree, sans-serif', lineHeight: 1 }}>
          {
            fmtGlyph(compDisplayPrice(comp))
          }
        </span>
      </div>
      {/* Downward pointer */}
      <div style={{
        width: 0, height: 0,
        borderLeft: '5px solid transparent',
        borderRight: '5px solid transparent',
        borderTop: `6px solid ${bg}`,
      }} />
    </div>
  )
}

// ── Subject property marker ────────────────────────────────────────────────────
function SubjectMarker() {
  return (
    <div style={{
      width: 32, height: 32, borderRadius: '50%',
      background: '#17270D',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      boxShadow: '0 2px 6px rgba(0,0,0,0.3)',
    }}>
      <span style={{ color: '#fff', fontSize: 14, fontWeight: 700, fontFamily: 'Figtree, sans-serif' }}>S</span>
    </div>
  )
}

// Comment Set #2, item 3.5. Paths are the export's own `btn_91/gallery` group
// (files/sow2-section-lean.svg), translated to a local 0,0 origin — not a generic icon.
// Comment Set #3, item 2: none of the three carried a strokeWidth, so they rendered at SVG's
// default of 1 — his own word, "1.2 to be thicker", is the value; no frame pins it.
//
// logic-auditor, 16.09: paths 1 and 3 both touch the original `0 0 13 11.15` viewBox edge, so half
// of their 1.2 stroke was clipped by the SVG viewport — only path 2 (the fully interior circle) ever
// painted at the full width, and the frame/lens shape is exactly what he called "almost illegible".
// The fix pads the viewBox by half the extra stroke on every side (0.6) and grows width/height by
// the same 1.2 in each dimension, so the rendered box's aspect ratio still matches the viewBox
// exactly and one user unit stays one CSS pixel -- padding the viewBox alone without matching
// width/height would have scaled the whole glyph down under `xMidYMid meet` and left the stroke
// thinner than 1.2 again. The 1.2px growth sits inside a 56x28 button with room to spare.
function GalleryGlyph() {
  return (
    <svg width="14.2" height="12.35" viewBox="-0.6 -0.6 14.2 12.35" fill="none" aria-hidden="true">
      <path d="M0.12 9.15L1.7 7.59C2.38 6.91 3.46 6.84 4.23 7.42L4.73 7.8C5.33 8.25 6.19 8.16 6.67 7.58L7.97 6.06C8.72 5.18 10.07 5.15 10.86 6L12.98 8.28" stroke="white" strokeWidth="1.2" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M4.49 4.85C5.2 4.85 5.78 4.27 5.78 3.55C5.78 2.83 5.2 2.25 4.49 2.25C3.77 2.25 3.19 2.83 3.19 3.55C3.19 4.27 3.77 4.85 4.49 4.85Z" stroke="white" strokeWidth="1.2" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M11.35 11.15C12.26 11.14 13 10.41 13 9.49V1.65C13 0.74 12.26 0 11.35 0H1.65C0.74 0 0 0.74 0 1.65V9.49C0 10.41 0.74 11.15 1.65 11.15H8.6" stroke="white" strokeWidth="1.2" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}

// ── Info window card ───────────────────────────────────────────────────────────
function CompInfoWindow({ comp, comps, onListingClick, onGalleryOpen, onToggleArchived }) {
  const [bodyHovered, setBodyHovered] = useState(false)
  const [imgHovered, setImgHovered] = useState(false)
  const { street, city, state: stCode, zip } = comp.address ?? {}
  const addressLine = [street, city, stCode, zip].filter(Boolean).join(', ')
  const { beds, baths, sqft } = comp.specs ?? {}
  const photo = comp.photos?.[0]?.href
  const photoCount = comp.photos?.length ?? 0
  const statusLower = comp.status?.toLowerCase() ?? ''
  const statusColor = statusLower === 'sold' ? '#3FAE16' : statusLower === 'active' ? '#C13A4A' : '#E8B611'
  const specsSegments = [
    beds  && { val: beds,                              label: ' bd' },
    baths && { val: baths,                             label: ' ba' },
    sqft  && { val: Number(sqft).toLocaleString(),     label: ' sqft' },
  ].filter(Boolean)

  const handleListingClick = (e) => {
    e.stopPropagation()
    const idx = comps.findIndex(c => c.propertyId === comp.propertyId)
    onListingClick?.(comp, idx)
  }

  const handlePhotoClick = (e) => {
    e.stopPropagation()
    const photos = comp.photos ?? []
    if (photos.length > 0) onGalleryOpen?.(photos, comp.propertyId)
  }

  return (
    // negative margin counters Google Maps InfoWindow default padding. borderRadius matches
    // Google's own `.gm-style-iw-c` container (8px) -- at 12 the two arcs disagreed enough to leave
    // a ~1.17px pale crescent at each top corner, qa-verifier's live measurement, Comment Set #2 3.7.
    <div style={{ width: 270, borderRadius: 8, overflow: 'hidden', fontFamily: 'Figtree, sans-serif' }}>
      {/* Photo row */}
      <div style={{ position: 'relative', aspectRatio: '3/2', background: '#E7F0E2', overflow: 'hidden' }}>
        {photo
          ? <img
              src={photo} alt=""
              onClick={handlePhotoClick}
              onMouseEnter={() => setImgHovered(true)}
              onMouseLeave={() => setImgHovered(false)}
              style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block', cursor: comp.photos?.length > 0 ? 'pointer' : 'default', opacity: imgHovered ? .9 : 1 }}
            />
          : <div style={{ width: '100%', height: '100%', background: '#E7F0E2' }} />
        }
        {/* Status badge */}
        <span style={{
          position: 'absolute',   
          top: '15px', 
          left: '14px',
          background: statusColor, 
          color: '#fff',
          fontSize: 13, 
          padding: '2px 4px', 
          borderRadius: '.25rem',
          letterSpacing: '0.03em',
        }}>
          {(comp.status ?? '').toUpperCase()}
        </span>

        {/* Photo count. His requirement, literal: "Do not show photo count button on the map
            property card if there is no photo or only one photo" — with one photo there is nothing
            for the button to open that clicking the photo itself does not already do, and with none
            it would read `0`. Measured on production 20.08: 152 of 528 saved Sold comps carry
            exactly one photo and 64 comps carry none, so both suppressed cases are the common ones
            rather than corners. */}
        {photoCount > 1 && (
          <button
            onClick={handlePhotoClick}
            style={{
              position: 'absolute',
              bottom: '12px',
              right: '14px',
              width: 56,
              height: 28,
              background: 'rgba(0,0,0,0.3)',
              color: '#fff',
              fontSize: 13,
              fontWeight: 500,
              borderRadius: 14,
              cursor: 'pointer',
              border: 'none',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 4,
            }}
          >
            <GalleryGlyph />
            {photoCount}
          </button>
        )}
      </div>

      {/* Body */}
      <div
        style={{ padding: '8px 8px 12px', background: '#fff', cursor: 'pointer' }}
        onClick={handleListingClick}
        onMouseEnter={() => setBodyHovered(true)}
        onMouseLeave={() => setBodyHovered(false)}
      >
        <p style={{ fontSize: 20, fontWeight: 800, color: '#17270D', margin: 0, lineHeight: 1.2 }}>
          { fmtPrice(compDisplayPrice(comp)) }
        </p>
        <p style={{ fontSize: 14, fontWeight: 400, color: '#17270D', margin: '0 0 1px', lineHeight: 1.4 }}>
          {specsSegments.map((seg, i) => (
            <span key={i}>
              {i > 0 && <span> · </span>}
              <span style={{ fontWeight: 700 }}>{seg.val}</span>
              <span>{seg.label}</span>
            </span>
          ))}
        </p>
        <p style={{ fontSize: 14, fontWeight: 400, color: bodyHovered ? '#3AA015' : '#4F5F4A', margin: 0, lineHeight: 1.4, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {addressLine}
        </p>
      </div>
    </div>
  )
}

// ── Main component ─────────────────────────────────────────────────────────────
export default function ArvMap({ coordinates, comps = [], onListingClick, onGalleryOpen, onToggleArchived }) {
  const [activeCompId, setActiveCompId] = useState(null)
  const activeComp = comps.find(c => c.propertyId === activeCompId) ?? null

  // DEFAULT_CENTER is a viewport fallback only — it points the camera somewhere valid when the
  // subject has no coordinates on file (Add Property's lookup failing, or a manually-typed
  // address). It must never stand in for the subject's own pin: that would show a specific,
  // confident location for a property that isn't there, the same fabricated-as-real failure as
  // the mock comps this file's caller used to fall back to.
  const hasSubjectCoordinates = coordinates?.lat != null && (coordinates?.lon ?? coordinates?.lng) != null
  const center = hasSubjectCoordinates
    ? { lat: coordinates.lat, lng: coordinates.lon ?? coordinates.lng }
    : DEFAULT_CENTER

  // Without the subject's coordinates the camera used to open on DEFAULT_CENTER at zoom 15 — a
  // street-level view of downtown Denver — while every comp marker this component was drawing sat
  // off-screen, in whatever state the search actually ran in. The map looked empty and broken. Fall
  // back to the comps' own extent instead, so the camera opens on the pins that exist. DEFAULT_CENTER
  // is only reached now when there are no coordinates anywhere, and then there is nothing to miss.
  const compPositions = comps
    .map(c => c.listingCoord)
    .filter(p => Number.isFinite(p?.lat) && Number.isFinite(p?.lng))

  const compExtent = compPositions.length ? {
    north: Math.max(...compPositions.map(p => p.lat)),
    south: Math.min(...compPositions.map(p => p.lat)),
    east:  Math.max(...compPositions.map(p => p.lng)),
    west:  Math.min(...compPositions.map(p => p.lng)),
  } : null

  // Mount only. @vis.gl/react-google-maps reads defaultCenter/defaultZoom/defaultBounds once, when
  // it constructs the map, and discards every later change to them — so this covers opening the
  // screen, and a Search on an already-open screen leaves the camera where it was. Moving it then
  // needs useMap() and an imperative fitBounds, which is mr-03's when it next touches the map.
  //
  // A single comp — or several at the same address — gives a zero-area box, and fitBounds on one
  // of those zooms in as far as the map allows. Centre on the point at the normal zoom instead.
  const extentIsAPoint = compExtent
    && compExtent.north === compExtent.south
    && compExtent.east === compExtent.west

  const cameraProps = hasSubjectCoordinates || !compExtent
    ? { defaultCenter: center, defaultZoom: 15 }
    : extentIsAPoint
      ? { defaultCenter: { lat: compExtent.north, lng: compExtent.east }, defaultZoom: 15 }
      : { defaultBounds: { ...compExtent, padding: 48 } }

  if (!MAPS_KEY) {
    return (
      <div
        className="w-full rounded-2xl bg-brand-muted flex items-center justify-center"
        style={{ height: 600, maxWidth: 1200 }}
      >
        <p className="text-[13px] text-brand-unselectedGray">
          Add <code className="bg-white px-[4px] py-[2px] rounded text-brand-dark">REACT_APP_GOOGLE_MAPS_API_KEY</code> to .env to enable the map
        </p>
      </div>
    )
  }

  return (
    // Comment Set #2, item 3.6: #DCEBD4 was a wrong token here; brand-fieldBdr (#C6CFC6) is what
    // this screen's own fields and filters already carry.
    <div className="w-full rounded-2xl overflow-hidden border border-brand-fieldBdr" style={{ height: 620, maxWidth: 1200 }}>
      <APIProvider apiKey={MAPS_KEY}>
        <Map
          mapId={MAP_ID}
          {...cameraProps}
          gestureHandling="greedy"
          fullscreenControl={false}
          mapTypeControl={false}
          renderingType='VECTOR'
          disableDefaultUI={true}
          style={{ width: '100%', height: '642px' }}
          onClick={() => setActiveCompId(null)}
        >
          {/* Subject property — only when we have its real coordinates; otherwise center is
              DEFAULT_CENTER and a pin there would claim a location that isn't the subject's. */}
          {hasSubjectCoordinates && (
            <AdvancedMarker position={center}>
              <SubjectMarker />
            </AdvancedMarker>
          )}

          {/* Comp markers */}
          {comps.map(comp =>
            comp.listingCoord?.lat && comp.listingCoord?.lng ? (
              <AdvancedMarker
                key={comp.propertyId}
                position={comp.listingCoord}
                onClick={() => setActiveCompId(prev =>
                  prev === comp.propertyId ? null : comp.propertyId
                )}
              >
                <CompMarker
                  comp={comp}
                  active={activeCompId === comp.propertyId}
                />
              </AdvancedMarker>
            ) : null
          )}

          {/* Info window */}
          {activeComp?.listingCoord && (
            <InfoWindow
              onCloseClick={() => setActiveCompId(null)}
              position={activeComp.listingCoord}
              pixelOffset={[0,-30]}
              headerDisabled={true}
              maxWidth={272}
              style={{overflowX:'hidden'}}
            >
              <CompInfoWindow
                comp={activeComp}
                comps={comps}
                onListingClick={onListingClick}
                onGalleryOpen={onGalleryOpen}
                onToggleArchived={onToggleArchived}
              />
            </InfoWindow>
          )}
        </Map>
      </APIProvider>
    </div>
  )
}
